import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  OutlinedInput,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// [Web UX 9 / T2, 2026-10-13] a dátumválasztók magyar feliratai (Cancel/OK/Select date helyett) — lásd utils/dateLocale.ts
import { HU_PICKER_LOCALE_TEXT } from '../../utils/dateLocale';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { hu } from 'date-fns/locale';
import coachAvailabilityService, {
  CreateCoachAvailabilityDto,
  CoachAvailabilitySeriesResultDto,
  VenueDto
} from '../../services/coachAvailabilityService';
import eventService from '../../services/eventService';
import { userService } from '../../services/userService';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../../utils/formDirty';
import { requestErrorMessage, saveErrorMessage } from '../../utils/saveErrors';
import { formatDateToLocal } from '../../utils/dateUtils';

/** Egy sorozat legfeljebb ennyi alkalom (a szerver is ezt ellenőrzi) — elgépelt „eddig" dátum ellen. */
export const MAX_SERIES_OCCURRENCES = 26;

/** Hány alkalom jön létre: az első naptól hetenként (intervalWeeks) az „eddig" napig bezárólag. */
export function seriesOccurrenceCount(firstDate: string, untilDate: string, intervalWeeks: number): number {
  const first = new Date(firstDate + 'T00:00:00');
  const until = new Date(untilDate + 'T00:00:00');
  if (isNaN(first.getTime()) || isNaN(until.getTime()) || until < first || intervalWeeks < 1) return 0;
  const days = Math.round((until.getTime() - first.getTime()) / 86400000);
  return Math.floor(days / (7 * intervalWeeks)) + 1;
}

/**
 * Az ELŐKÉSZÍTETT (még ki nem töltött) űrlap — pontosan az, amit a nyitáskori reset beállít.
 * Egy helyen, hogy a mentetlen-változás ALAPVONALA ne csúszhasson el a reset értékeitől.
 */
export function emptyAvailabilityForm(initialDate?: string): CreateCoachAvailabilityDto {
  return {
    // Helyi nap, nem UTC [NB-6, 2026-09-13]: a `toISOString()` 00:00–02:00 budapesti idő között
    // a TEGNAPI napot adta, tehát az űrlap éjfél után a tegnapi dátummal nyílt.
    availabilityDate: initialDate || formatDateToLocal(new Date()),
    startTime: '09:00',
    endTime: '17:00',
    notes: '',
    venueIds: [],
  };
}

interface CoachAvailabilityCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialDate?: string;
  userRole?: string;
}

interface UserDto {
  userId: string;
  name: string;
  userRole: string;
  isActive: boolean;
}

const CoachAvailabilityCreateDialog: React.FC<CoachAvailabilityCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
  initialDate,
  userRole = ''
}) => {
  const [formData, setFormData] = useState<CreateCoachAvailabilityDto>(() => emptyAvailabilityForm(initialDate));
  const [venues, setVenues] = useState<VenueDto[]>([]);
  const [coaches, setCoaches] = useState<UserDto[]>([]);
  const [selectedCoachId, setSelectedCoachId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  // Heti ismétlés [UX 2026-09-12]: „hétfő és szerda 16–20" egy mentéssel, nem hetenként külön.
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatUntil, setRepeatUntil] = useState<string>('');
  const [intervalWeeks, setIntervalWeeks] = useState<number>(1);
  const [seriesResult, setSeriesResult] = useState<CoachAvailabilitySeriesResultDto | null>(null);
  const { showSuccess } = useFeedback();
  const confirm = useConfirm();

  const isAdmin = userRole === 'Administrator';

  /**
   * Mentetlen-változás védelem. Az alapvonal az ELŐKÉSZÍTETT űrlap (a naptárból örökölt dátum és
   * a 09:00–17:00 alapérték NEM felhasználói bevitel).
   *
   * `open &&`: zárva is mountolva marad a szülőben. `!seriesResult`: ha megjelent a sorozat-összegzés,
   * a MENTÉS MÁR MEGTÖRTÉNT (a dialógus csak azért van nyitva, hogy az edző lássa, mely hét maradt
   * ki) — ott a bezárásra rákérdezni hazugság lenne, és elrontaná az `onSuccess`-t is.
   */
  const currentSnapshot = {
    form: formData,
    selectedCoachId,
    repeatWeekly,
    repeatUntil,
    intervalWeeks,
  };
  const baselineSnapshot: typeof currentSnapshot = {
    form: emptyAvailabilityForm(initialDate),
    selectedCoachId: '',
    repeatWeekly: false,
    repeatUntil: '',
    intervalWeeks: 1,
  };
  const isDirty = open && !seriesResult && isFormDirty(baselineSnapshot, currentSnapshot);
  useUnsavedChanges(isDirty, 'Edzői elérhetőség');

  // Hány alkalom jönne létre (a szerverrel azonos szabály: az „eddig" nap bezárólag).
  const plannedOccurrences = repeatWeekly && repeatUntil && repeatUntil > formData.availabilityDate
    ? seriesOccurrenceCount(formData.availabilityDate, repeatUntil, intervalWeeks)
    : 0;

  useEffect(() => {
    if (open) {
      loadVenues();
      if (isAdmin) {
        loadCoaches();
      }
      // Reset form data when dialog opens with correct date
      setFormData(emptyAvailabilityForm(initialDate));
      setSelectedCoachId('');
      setRepeatWeekly(false);
      setRepeatUntil('');
      setIntervalWeeks(1);
      setSeriesResult(null);
    }
  }, [open, initialDate, isAdmin]);

  const loadVenues = async () => {
    try {
      const response = await eventService.getVenues();
      console.log('Venue response received:', response);

      // Check if response has data property (API wrapper format)
      const venueData = (response as any)?.data || response;

      if (Array.isArray(venueData)) {
        setVenues(venueData.filter((venue: any) => venue.isActive));
      } else {
        console.error('Venue data is not an array:', venueData);
        setError('Hiba történt a helyszínek betöltésekor: nem megfelelő adatformátum');
        setVenues([]);
      }
    } catch (err) {
      console.error('Error loading venues:', err);
      // [Web UX 3. csomag (D), 2026-10-04] betöltési / nem-űrlap hiba: a lejárat-tudatos `requestErrorMessage` (a fájl mentetlen-
      // védelmes, ezért a közvetlen `apiErrorMessage` itt tiltott — `unsavedCoverage.test.ts`).
      setError(requestErrorMessage(err, 'Hiba történt a helyszínek betöltésekor'));
      setVenues([]);
    }
  };

  const loadCoaches = async () => {
    try {
      const response = await userService.getCoaches();
      const coachData = (response as any)?.data || response;
      if (Array.isArray(coachData)) {
        setCoaches(coachData.filter((coach: any) => coach.isActive).sort((a: UserDto, b: UserDto) => a.name.localeCompare(b.name, 'hu')));
      } else {
        console.error('Coach data is not an array:', coachData);
        setCoaches([]);
      }
    } catch (err) {
      console.error('Error loading coaches:', err);
      setCoaches([]);
    }
  };

  const handleDateChange = (date: any) => {
    if (date) {
      const dateObj = date.$d || date;
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      setFormData(prev => ({
        ...prev,
        availabilityDate: `${year}-${month}-${day}`
      }));
    }
  };

  const handleStartTimeChange = (time: any) => {
    if (time) {
      const timeObj = time.$d || time;
      const timeString = timeObj.toTimeString().slice(0, 5);
      setFormData(prev => ({ ...prev, startTime: timeString }));
    }
  };

  const handleEndTimeChange = (time: any) => {
    if (time) {
      const timeObj = time.$d || time;
      const timeString = timeObj.toTimeString().slice(0, 5);
      setFormData(prev => ({ ...prev, endTime: timeString }));
    }
  };

  const handleCoachChange = (event: SelectChangeEvent) => {
    setSelectedCoachId(event.target.value as string);
  };

  const handleVenueChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      venueIds: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, notes: event.target.value }));
  };

  const validateForm = (): boolean => {
    if (!formData.availabilityDate) {
      setError('Kérjük válasszon dátumot');
      return false;
    }
    if (!formData.startTime) {
      setError('Kérjük adja meg a kezdési időt');
      return false;
    }
    if (!formData.endTime) {
      setError('Kérjük adja meg a befejezési időt');
      return false;
    }
    if (formData.startTime >= formData.endTime) {
      setError('A befejezési idő később kell legyen, mint a kezdési idő');
      return false;
    }
    if (formData.venueIds.length === 0) {
      setError('Kérjük válasszon legalább egy helyszínt');
      return false;
    }
    if (repeatWeekly) {
      // NaN-NaN-NaN (részlegesen gépelt dátum) is fennakad: az alkalom-szám 0 lenne.
      if (!repeatUntil || repeatUntil <= formData.availabilityDate || plannedOccurrences < 2) {
        setError('Az „eddig” dátum legalább egy héttel az első alkalom után legyen');
        return false;
      }
      if (plannedOccurrences > MAX_SERIES_OCCURRENCES) {
        setError(`Egy sorozat legfeljebb ${MAX_SERIES_OCCURRENCES} alkalomból állhat — válassz közelebbi „eddig” dátumot`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Add coachId to form data if admin has selected a coach
      const submitData: CreateCoachAvailabilityDto = {
        ...formData,
        ...(isAdmin && selectedCoachId ? { coachId: selectedCoachId } : {})
      };

      if (repeatWeekly) {
        const result = await coachAvailabilityService.createCoachAvailabilitySeries({
          ...submitData,
          repeatUntil,
          intervalWeeks,
        });
        // Ha volt kihagyott (ütköző) nap, a dialógus NYITVA marad az összegzéssel, hogy az edző lássa,
        // melyik hét maradt ki. Az `onSuccess`-t ilyenkor NEM most hívjuk: a beágyazó nézetek abban
        // zárják a dialógust (setCreateDialogOpen(false)), és az összegzés sosem látszana (validátor).
        // A „Bezárás" gomb hívja majd az onSuccess-t (naptár-frissítés + zárás).
        if (result.skipped.length === 0) {
          showSuccess(`${result.created.length} alkalom létrehozva.`);
          onSuccess();
          closeWithoutAsking();
        } else {
          setSeriesResult(result);
        }
        return;
      }
      await coachAvailabilityService.createCoachAvailability(submitData);
      showSuccess('Az elérhetőség létrehozva.');
      onSuccess();
      closeWithoutAsking();
    } catch (err: any) {
      console.error('Error creating coach availability:', err);
      // [validátor M-1 / 3. javító kör F-1, 2026-10-03] lejárt munkamenet → az újra-belépő ablakkal
      // egyező mondat; a közös, zár-tudatos burkolón át (`utils/saveErrors.ts`).
      setError(saveErrorMessage(err, 'Hiba történt az elérhetőség létrehozásakor'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Kérdés NÉLKÜLI zárás — a SIKERES mentés és a sorozat-összegzés „Bezárás" gombja használja.
   *
   * ⚠️ A NÉV SZÁNDÉKOSAN beszédes [NB-5, 2026-09-13]: ez a függvény korábban `handleClose` volt,
   * az őrzött út pedig `handleUserClose` — pont FORDÍTVA, mint a többi dialógusban (ott a
   * `handleClose` az őrzött, felhasználói út). Egy jövőbeli „bezárás" gomb a megszokás szerint a
   * `handleClose`-ra került volna, és NÉMÁN kikerüli a mentetlen-változás kaput. A felhasználó
   * minden záró útja a `handleClose`-on megy, a kérdés nélküli zárás neve pedig kimondja magát.
   */
  const closeWithoutAsking = () => {
    setFormData(emptyAvailabilityForm());
    setSelectedCoachId('');
    setError('');
    setRepeatWeekly(false);
    setRepeatUntil('');
    setIntervalWeeks(1);
    setSeriesResult(null);
    onClose();
  };

  /**
   * A FELHASZNÁLÓ záró útjai: a „Mégse" gomb és a Dialog `onClose`-a (backdrop + ESC).
   * Beírt, még nem mentett adatnál a közös megerősítő ablak kérdez.
   */
  const handleClose = async () => {
    if (isDirty && !(await confirm(discardChangesConfirm('elérhetőség űrlapja')))) {
      return;
    }
    closeWithoutAsking();
  };

  const handleRepeatUntilChange = (date: any) => {
    if (date) {
      const dateObj = date.$d || date;
      // Részlegesen gépelt szekciós mező Invalid Date-et ad → ne tároljunk „NaN-NaN-NaN"-t.
      if (typeof dateObj.getTime !== 'function' || isNaN(dateObj.getTime())) {
        setRepeatUntil('');
        return;
      }
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      setRepeatUntil(`${year}-${month}-${day}`);
    }
  };

  const parseTime = (timeString: string): Date => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  const parseDate = (dateString: string): Date => {
    return new Date(dateString + 'T00:00:00');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu} localeText={HU_PICKER_LOCALE_TEXT}>
      {/* Összegző módban (részleges siker) az ESC/háttér-zárás is frissítse a naptárat — az alkalmak már léteznek. */}
      <Dialog open={open} onClose={seriesResult ? () => { onSuccess(); closeWithoutAsking(); } : handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Új Edző Elérhetőség</DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {isAdmin && (
              <FormControl fullWidth>
                <InputLabel>Edző (opcionális)</InputLabel>
                <Select
                  value={selectedCoachId}
                  label="Edző (opcionális)"
                  onChange={handleCoachChange}
                >
                  <MenuItem value="">
                    <em>Saját magam számára</em>
                  </MenuItem>
                  {coaches.map((coach) => (
                    <MenuItem key={coach.userId} value={coach.userId}>
                      {coach.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <DatePicker
              label="Dátum"
              value={parseDate(formData.availabilityDate)}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined'
                }
              }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TimePicker
                label="Kezdési idő"
                value={parseTime(formData.startTime)}
                onChange={handleStartTimeChange}
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />

              <TimePicker
                label="Befejezési idő"
                value={parseTime(formData.endTime)}
                onChange={handleEndTimeChange}
                ampm={false}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Helyszínek</InputLabel>
              <Select
                multiple
                value={formData.venueIds}
                onChange={handleVenueChange}
                input={<OutlinedInput label="Helyszínek" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const venue = venues.find(v => v.venueId === value);
                      return (
                        <Chip
                          key={value}
                          label={venue?.name || value}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {venues.map((venue) => (
                  <MenuItem key={venue.venueId} value={venue.venueId}>
                    {venue.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Megjegyzések (opcionális)"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleNotesChange}
              fullWidth
              variant="outlined"
            />

            {/* Heti ismétlés [UX 2026-09-12] */}
            <FormControlLabel
              control={
                <Switch
                  checked={repeatWeekly}
                  onChange={(e) => setRepeatWeekly(e.target.checked)}
                  disabled={!!seriesResult}
                  inputProps={{ 'aria-label': 'Ismétlés hetente' } as any}
                  data-testid="availability-repeat-switch"
                />
              }
              label="Ismétlés hetente (ugyanez az időpont minden héten)"
            />
            {repeatWeekly && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="availability-repeat-fields">
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <DatePicker
                    label="Eddig (bezárólag)"
                    value={repeatUntil ? parseDate(repeatUntil) : null}
                    onChange={handleRepeatUntilChange}
                    minDate={parseDate(formData.availabilityDate)}
                    disabled={!!seriesResult}
                    slotProps={{ textField: { fullWidth: true, variant: 'outlined', inputProps: { 'data-testid': 'availability-repeat-until' } } }}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="availability-repeat-interval-label">Gyakoriság</InputLabel>
                    <Select
                      labelId="availability-repeat-interval-label"
                      label="Gyakoriság"
                      value={String(intervalWeeks)}
                      onChange={(e) => setIntervalWeeks(Number(e.target.value))}
                      disabled={!!seriesResult}
                      data-testid="availability-repeat-interval"
                    >
                      <MenuItem value="1">Hetente</MenuItem>
                      <MenuItem value="2">Kéthetente</MenuItem>
                      <MenuItem value="3">Háromhetente</MenuItem>
                      <MenuItem value="4">Négyhetente</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="body2" color={plannedOccurrences > MAX_SERIES_OCCURRENCES ? 'error' : 'text.secondary'} data-testid="availability-repeat-count">
                  {plannedOccurrences > 0
                    ? `${plannedOccurrences} alkalom jön létre (${formData.availabilityDate} – ${repeatUntil}). Az ütköző napokat kihagyjuk és jelezzük.`
                    : 'Add meg, meddig ismétlődjön — az alkalmak az első naptól hetente jönnek létre.'}
                </Typography>
              </Box>
            )}
            {seriesResult && (
              <Alert severity={seriesResult.created.length > 0 ? 'warning' : 'error'} data-testid="availability-series-summary">
                {seriesResult.created.length} alkalom létrejött, {seriesResult.skipped.length} kimaradt:
                {' '}
                {seriesResult.skipped.map((s) => `${s.date} (${s.reason})`).join(', ')}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              * Az elérhetőség azt jelzi, hogy az edző elérhető edzések szervezéséhez a megadott időpontban és helyszínen/helyszíneken.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          {seriesResult ? (
            <Button onClick={() => { onSuccess(); closeWithoutAsking(); }} variant="contained" data-testid="availability-series-close">
              Bezárás
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={loading}>
                Mégse
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                data-testid="availability-submit"
              >
                {loading
                  ? 'Létrehozás...'
                  : repeatWeekly && plannedOccurrences > 0 ? `${plannedOccurrences} alkalom létrehozása` : 'Létrehozás'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CoachAvailabilityCreateDialog;